import { Check, Truck } from "lucide-react";
import { subPrice, productImage, productSrcSet, memberPrice } from "@/lib/api";
import { OCEAN, OCEAN_STORY, MEET_FINN, OCEAN_SHIP_NOTE } from "@/data/ocean";
import { SINGLES, SINGLE_STORY, STORY_NOTE } from "@/data/singles";
import { BUNDLES } from "@/data/bundles";
import { MembersGate } from "@/components/Members";

import { PurchaseOptions } from "@/components/ProductPanel";
import { ClaimNote, Warnings } from "@/components/Wave";
import { Usd, usePrice } from "@/components/Usd";
import { ServingInfo, LoyaltyLine, BundleSave, Spoonful, PackValue } from "@/components/ProductExtras";
import { PACK_VALUE, SPOONFUL } from "@/data/content";
import { MeetFinnBlock, WhyStack } from "@/components/WhyStack";
import { WHY } from "@/data/why";
import { productAlt } from "@/data/seo";
import { SEA_MOSS_IDS, SeaMossFacts, SeaMossDirections, SeaMossFaq, iodineLine } from "@/components/SeaMoss";

export const OceanShip = ({ id, us = false }) => (
  <p data-testid={us ? `us-only-${id}` : `ocean-ships-${id}`} className="inline-flex items-center gap-2 rounded-full bg-foam px-3 py-1 text-xs font-semibold"><Truck className="h-4 w-4" /> {us ? "US only" : "Ships to the US, Canada, Europe, Australia & NZ"}</p>
);

export const OceanPrice = ({ p, testId, value = false }) => {
  const price = usePrice();
  return (
    <p data-testid={testId} className="font-semibold">
      {price(p.price_usd)} one-time · {p.member_discount === false ? <span data-testid={`member-na-${p.id}`} className="text-sm font-normal">Member discount n/a</span> : <span data-testid={`member-price-${p.id}`} className="text-sm font-normal">Members {price(memberPrice(p))}</span>}
      {value && PACK_VALUE[p.id] ? <PackValue id={p.id} /> : <BundleSave p={p} className="mt-1" />}
    </p>
  );
};

const Science = ({ id, o, use }) => (
  <section id={`ocean-science-${id}`} data-testid={`ocean-science-${id}`} className="container-gf pb-12 scroll-mt-28">
    <span className="label-chip">The science</span>
    {o.claims.length > 0 && <ul data-testid={`ocean-claims-${id}`} className="mt-6 sticker-sm bg-foam/60 p-5 space-y-2 text-sm font-medium max-w-3xl">{o.claims.map((c) => <li key={c}>{c}</li>)}</ul>}
    <ClaimNote testId={`ocean-fda-${id}`} className="mt-3 max-w-3xl" />
    {o.research ? <Research id={id} items={o.research} /> : o.why && <><h3 className="mt-10 text-2xl">Why people take it</h3>
    <p data-testid={`ocean-why-${id}`} className="mt-3 text-sm leading-relaxed text-slate-700 max-w-3xl">{o.why}</p>
    {o.whyClaim && <><p data-testid={`ocean-why-claim-${id}`} className="mt-3 text-sm font-medium max-w-3xl">{o.whyClaim}</p><ClaimNote testId={`ocean-why-fda-${id}`} className="mt-3 max-w-3xl" /></>}</>}
    {use && <><h3 className="mt-10 text-2xl">Suggested use</h3><p data-testid={`ocean-use-${id}`} className="mt-3 text-sm leading-relaxed text-slate-700 max-w-3xl">{use}</p></>}
    {o.studies && <Research id={id} items={o.studies.map((r) => ({ ...r, label: "Source" }))} title="What the research says" />}
  </section>
);

const Research = ({ id, items, title = "Why it's here" }) => (
  <>
    <h3 className="mt-10 text-2xl">{title}</h3>
    <ul data-testid={`pack-research-${id}`} className="mt-3 space-y-3 text-sm leading-relaxed text-slate-700 max-w-3xl list-disc pl-5">
      {items.map((r, i) => <li key={r.url}>{r.text}{" "}<a href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`pack-source-${id}-${i + 1}`} className="font-semibold underline decoration-sunny decoration-2 underline-offset-4">{r.label}</a></li>)}
    </ul>
  </>
);

const PackAbout = ({ id, o }) => (
  <div data-testid={`pack-about-${id}`} className="mt-5 text-sm leading-relaxed text-slate-700">
    <p>{o.about}</p>
    {o.about.includes("*") && <ClaimNote testId={`pack-about-fda-${id}`} className="mt-3" />}
    <ul className="mt-3 list-disc pl-5 space-y-1">{o.bullets.map((b) => <li key={b}>{b}</li>)}</ul>
    <ClaimNote testId={`pack-fda-${id}`} className="mt-3" />
  </div>
);

const Labels = ({ id, o }) => (
  <section data-testid={`ocean-facts-${id}`} className="container-gf pb-12">
    <span className="label-chip">Supplement Facts</span>
    <div className="mt-6 grid gap-6 lg:grid-cols-2">
      {o.labels.map((l) => (
        <div key={l.name} className="rounded-3xl bg-white border-2 border-navy p-5">
          <p className="font-display text-lg leading-snug">{l.name}</p>
          <pre className="mt-3 whitespace-pre-wrap break-words text-[11px] leading-relaxed text-navy" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }}>{l.text}</pre>
        </div>
      ))}
    </div>
  </section>
);

export const OceanPanel = ({ p }) => {
  const o = OCEAN[p.id] || SINGLES[p.id] || BUNDLES[p.id];
  const sm = SEA_MOSS_IDS.includes(p.id);
  return (
    <>
      <section id={p.id} data-testid={`product-panel-${p.id}`} className="container-gf py-12 md:py-16 grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-start scroll-mt-28">
        <OceanImage p={p} hero className="lg:sticky lg:top-28 lg:aspect-square" />
        <div className="rise">
          <OceanShip id={p.id} us={!!p.ship_countries} />
          <h2 data-testid={`product-name-${p.id}`} className="mt-4 text-3xl md:text-5xl leading-[1.05]">{p.short_name}</h2>
          {o.tagline && <p data-testid={`ocean-tagline-${p.id}`} className="mt-2 text-lg text-slate-600">{o.tagline}</p>}
          {o.nameNote && <p data-testid={`name-note-${p.id}`} className="mt-2 text-sm italic text-slate-600">{o.nameNote}</p>}
          <p data-testid={`ocean-subtitle-${p.id}`} className="mt-2 text-lg text-slate-600">{o.subtitle || p.subtitle}</p>
          <div className="mt-5 text-xl"><OceanPrice p={p} testId={`product-price-${p.id}`} value /></div>
          <ServingInfo p={p} />
          {o.about && <PackAbout id={p.id} o={o} />}
          {o.alert && <p data-testid={`dose-alert-${p.id}`} role="alert" className="mt-4 rounded-2xl border-2 border-navy bg-sunny/40 p-4 text-sm font-semibold">{o.alert}</p>}
          <p className="mt-1 text-xs text-slate-600">SKU {o.sku} · {p.ship_countries ? "US only." : OCEAN_SHIP_NOTE}</p>
          <div data-testid={`stack-inside-${p.id}`} className="mt-6 sticker-sm bg-white p-5">
            <p className="font-display text-lg">What's inside</p>
            <ul className="mt-2 space-y-1 text-sm">{p.contents.map((c) => <li key={c} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={3} />{c}</li>)}</ul>
          </div>
          {sm && <><SeaMossFacts id={p.id} /><SeaMossDirections id={p.id} /></>}
          {p.coming_soon ? (
            <div data-testid={`coming-soon-block-${p.id}`} className="mt-8 rounded-3xl border border-dashed border-navy/25 bg-sand/60 p-6">
              <span className="label-chip">Coming soon</span>
              <p className="mt-3 text-sm text-slate-600">Not available to buy yet. The full label is still being checked.</p>
            </div>
          ) : p.members_only ? <MembersGate p={p}><PurchaseOptions p={p} /></MembersGate> : <PurchaseOptions p={p} />}
          {!p.coming_soon && <LoyaltyLine testId={`loyalty-line-${p.id}`} className="mt-4" />}
          {sm && <SeaMossFaq id={p.id} />}
          <div className="mt-6"><Warnings text={<>{o.warnings.map((w) => <span key={w} className="block mt-2 first:mt-0">{w}{iodineLine(p.id, w)}</span>)}</>} testId={`warnings-block-${p.id}`} /></div>
        </div>
      </section>
      {(o.claims.length > 0 || o.research || o.why) && <Science id={p.id} o={o} use={SINGLES[p.id] && p.suggested_use} />}
      {SPOONFUL[p.id] && <div className="container-gf pb-12"><div className="max-w-3xl"><Spoonful id={p.id} /></div></div>}
      {!sm && <Labels id={p.id} o={o} />}
      {WHY[p.id] && <><MeetFinnBlock id={p.id} /><WhyStack id={p.id} /></>}
      <section data-testid={`ocean-story-${p.id}`} className="bg-white/60 border-t border-navy/10">
        <div className="container-gf section max-w-3xl">
          {o.meet ? <><span className="label-chip">Meet Grandpa Finn</span>
          <p data-testid={`ocean-meet-${p.id}`} className="mt-6 font-display text-2xl leading-snug">{o.meet}</p></> : <>
          <span className="label-chip">Grandpa's story</span>
          <p data-testid={`ocean-story-text-${p.id}`} className="mt-6 font-display text-2xl leading-snug">{o.story ? STORY_NOTE : SINGLES[p.id] ? SINGLE_STORY : OCEAN_STORY}</p>
          {!WHY[p.id] && <><h3 className="mt-12 text-2xl">Meet Grandpa Finn</h3>
          <p data-testid={`ocean-meet-${p.id}`} className="mt-3 leading-relaxed text-slate-700">{MEET_FINN}</p></>}</>}
        </div>
      </section>
    </>
  );
};

export const OceanImage = ({ p, className = "", compact = false, hero = false }) => (
  <img data-testid={`ocean-art-${p.id}`} src={productImage(p.id, 800)} srcSet={productSrcSet(p.id)} sizes={hero ? "(max-width: 1024px) 100vw, 560px" : "(max-width: 640px) 90vw, 400px"}
    alt={productAlt(p)} {...(hero ? { fetchpriority: "high" } : { loading: "lazy", decoding: "async" })} width="800" height="800"
    className={`${compact ? "rounded-3xl" : "sticker"} w-full aspect-square object-cover bg-white ${className}`} />
);
